// frontend/src/pages/SuppressionCompte.js — Suppression de compte et de données
//
// Google Play requires every app that lets users create an account to publish a
// data-deletion page, and to give its URL in the Data safety form. The
// requirements are specific, and a line in the privacy policy pointing at an
// email address is routinely rejected:
//
//   - reachable WITHOUT signing in (a reviewer has no account)
//   - about account deletion specifically, not general privacy
//   - says which data is deleted and which is kept, and for how long
//   - gives a mechanism the user can actually follow
//
// The retention half matters as much as the deletion half here. A workshop's
// invoices, receipts and cashbook entries are accounting records with a legal
// retention period in Côte d'Ivoire, and the platform deliberately cancels
// documents rather than deleting them — a gap in a numbered sequence is the
// first thing an auditor asks about. Claiming "everything is erased" would be
// untrue, and Play compares the claim against the privacy policy.

import React from "react";
import { useNavigate } from "react-router-dom";
import LegalPage, { Section, P, B, UL, LI, Table } from "../components/LegalPage";

const SuppressionCompte = () => {
  const navigate = useNavigate();
  const mailto =
    "mailto:contact@1parc.com" +
    "?subject=" + encodeURIComponent("Demande de suppression de compte 1PARC") +
    "&body=" + encodeURIComponent(
      "Bonjour,\n\n" +
      "Je demande la suppression de mon compte 1PARC et des donnees associees.\n\n" +
      "Nom complet : \n" +
      "Adresse e-mail du compte : \n" +
      "Numero de telephone du compte : \n" +
      "Immatriculation du ou des vehicules (facultatif) : \n\n" +
      "Je comprends que les documents comptables deja emis (factures, recus) sont " +
      "conserves pendant la duree legale, comme indique sur la page de suppression.\n\n" +
      "Cordialement,\n"
    );

  return (
    <LegalPage
      title="Suppression de compte"
      subtitle="Comment demander la suppression de votre compte 1PARC et des données associées, ce qui est effacé, et ce que nous sommes tenus de conserver."
      lastUpdated="18 octobre 2026"
    >
      <Section n={1} title="Demander la suppression">
        <P>
          Vous pouvez demander à tout moment la suppression de votre compte 1PARC
          (application mobile <B>1PARC</B> et site www.1parc.com) et des données
          personnelles qui s'y rattachent. Aucune justification n'est nécessaire.
        </P>
        <P>
          Envoyez votre demande à <B>contact@1parc.com</B> depuis l'adresse e-mail
          de votre compte, ou par téléphone au <B>0799290000</B>.
        </P>
        {/* Plain div, not <P>: LegalPage's P takes only `children`, so a style
            prop on it is silently dropped. */}
        <div style={{ margin: "18px 0" }}>
          <a
            href={mailto}
            style={{
              display: "inline-block",
              padding: "12px 22px",
              borderRadius: 10,
              background: "#06B6D4",
              color: "#07232A",
              fontWeight: 700,
              textDecoration: "none",
            }}
          >
            Rédiger ma demande de suppression
          </a>
        </div>
        <P>
          Pour protéger votre compte, nous vérifions votre identité avant toute
          suppression — en pratique, en confirmant que la demande provient bien de
          l'adresse e-mail ou du numéro enregistré. Précisez dans votre message :
        </P>
        <UL>
          <LI>votre nom complet ;</LI>
          <LI>l'adresse e-mail et le numéro de téléphone du compte ;</LI>
          <LI>l'immatriculation de vos véhicules, si vous souhaitez aussi les retirer.</LI>
        </UL>
        <P>
          Nous accusons réception sous <B>7 jours</B> et traitons la demande sous
          <B> 30 jours</B> au plus.
        </P>
      </Section>

      <Section n={2} title="Ce qui est supprimé">
        <P>À l'issue du traitement, nous supprimons définitivement :</P>
        <UL>
          <LI>votre compte et vos identifiants de connexion (mot de passe, jetons d'accès) ;</LI>
          <LI>vos coordonnées : nom, adresse e-mail, numéro de téléphone ;</LI>
          <LI>pour les professionnels : raison sociale, numéro fiscal, logo ;</LI>
          <LI>les fiches de vos véhicules : immatriculation, VIN, marque, modèle, kilométrage, dates d'assurance et de contrôle technique ;</LI>
          <LI>les photographies de vos véhicules ;</LI>
          <LI>vos notifications, vos préférences et vos jetons d'appareil (fin des notifications push et des SMS) ;</LI>
          <LI>les positions GPS enregistrées pour vos demandes de remorquage.</LI>
        </UL>
      </Section>

      <Section n={3} title="Ce que nous sommes tenus de conserver">
        <P>
          Certaines données ne peuvent pas être effacées à la demande, parce que la
          loi nous impose de les garder. Elles sont alors <B>dissociées de votre
          identité</B> : votre nom et vos coordonnées sont retirés, et seul subsiste
          le document comptable lui-même.
        </P>
        <Table
          head={["DONNÉE", "DURÉE", "POURQUOI"]}
          rows={[
            ["Factures et reçus de paiement émis", "10 ans", "Obligation comptable et fiscale (OHADA / Côte d'Ivoire)"],
            ["Écritures du livre de caisse", "10 ans", "Même obligation ; le journal ne peut comporter de trou"],
            ["Bons de livraison signés", "10 ans", "Preuve de remise du véhicule en cas de litige"],
            ["Historique d'entretien d'un véhicule", "Durée de vie du véhicule", "Rattaché au véhicule et non à vous ; anonymisé"],
            ["Journaux de sécurité (connexions, adresse IP)", "12 mois", "Sécurité et prévention de la fraude"],
          ]}
        />
        <P>
          Un document comptable est <B>annulé, jamais effacé</B> : une facture ou un
          reçu porte un numéro dans une série continue, et un numéro manquant est la
          première chose qu'un contrôleur relève. Ces documents restent également
          entre les mains de l'atelier qui les a émis, qui en est responsable de son
          côté.
        </P>
      </Section>

      <Section n={4} title="Si un atelier a créé votre compte">
        <P>
          Un atelier partenaire peut avoir enregistré votre véhicule et créé un
          compte préliminaire à partir des informations que vous lui avez
          communiquées, avant même que vous n'ayez activé ce compte. La présente
          procédure s'applique de la même manière : écrivez-nous et nous
          supprimerons ces données, que vous ayez activé le compte ou non.
        </P>
      </Section>

      {/* Google Play's Data deletion block asks for TWO links: one to delete
          the account and everything with it, and one to delete SOME data while
          keeping the account. This section is what lets a single URL answer
          both honestly. */}
      <Section n={5} title="Supprimer certaines données sans supprimer le compte">
        <P>
          Vous n'êtes pas obligé de tout supprimer. Vous pouvez retirer une partie
          de vos données et garder votre compte :
        </P>
        <UL>
          <LI>
            <B>Un véhicule</B> : depuis l'application mobile, ouvrez le véhicule puis
            supprimez-le. Sa fiche, ses photos et ses rappels sont retirés de votre
            compte.
          </LI>
          <LI>
            <B>Des photographies</B> : écrivez-nous en précisant le véhicule concerné
            et nous les supprimons.
          </LI>
          <LI>
            <B>Vos positions GPS</B> : les localisations enregistrées lors de demandes
            de remorquage peuvent être effacées sur simple demande.
          </LI>
          <LI>
            <B>Les SMS et e-mails</B> : nous pouvons désactiver ces envois en gardant
            votre compte et votre historique intacts. Les messages strictement
            nécessaires à la sécurité (codes de vérification) restent indispensables.
          </LI>
        </UL>
        <P>
          Pour toute demande partielle, utilisez la même adresse —
          <B> contact@1parc.com</B> — en indiquant précisément ce que vous souhaitez
          voir supprimé. Les règles de conservation de la section 3 s'appliquent de
          la même manière : un document comptable déjà émis ne peut pas être retiré.
        </P>
      </Section>

      <Section n={6} title="Conséquences de la suppression">
        <P>
          La suppression du compte est <B>définitive et irréversible</B>. Vous perdez
          l'accès à l'historique d'entretien de vos véhicules, à vos devis et à vos
          factures. Nous vous conseillons de télécharger les documents que vous
          souhaitez conserver avant d'en faire la demande.
        </P>
      </Section>

      <Section n={7} title="Contact">
        <P>
          <B>1 PARC SARL</B> — 2 Plateaux Paillet, Abidjan, Côte d'Ivoire —
          <B> contact@1parc.com</B> — <B>0799290000</B>.
        </P>
        <P>
          Pour le détail de nos traitements, consultez notre{" "}
          <button
            className="lk"
            onClick={() => navigate("/confidentialite")}
            style={{ background: "none", border: "none", padding: 0, cursor: "pointer", font: "inherit" }}
          >
            Politique de Confidentialité
          </button>
          . Vous pouvez également saisir l'<B>ARTCI</B>, autorité de protection des
          données en Côte d'Ivoire.
        </P>
      </Section>
    </LegalPage>
  );
};

export default SuppressionCompte;
